import React, { useState } from 'react';
import axios from 'axios';
import { PlusCircle, Save, AlertCircle, ToggleLeft, ToggleRight } from 'lucide-react';
import '../styles/postques.css';

const AddQuestionsForm = () => {
  const [questions, setQuestions] = useState([
    { question: '', options: ['', '', '', ''], answer: 0, hasOptions: true }
  ]);
  const [questionId, setQuestionId] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  const handleQuestionChange = (index, event) => {
    const values = [...questions];
    if (event.target.name === 'question') {
      values[index].question = event.target.value;
    } else if (event.target.name.startsWith('option')) {
      const optionIndex = parseInt(event.target.name.split('-')[1], 10);
      values[index].options[optionIndex] = event.target.value;
    } else if (event.target.name === 'answer') {
      values[index].answer = parseInt(event.target.value, 10);
    }
    setQuestions(values);
  };

  const toggleOptions = (index) => {
    const values = [...questions];
    values[index].hasOptions = !values[index].hasOptions;
    if (!values[index].hasOptions) {
      values[index].options = [];
      values[index].answer = 0;
    } else {
      values[index].options = ['', '', '', ''];
      values[index].answer = 0;
    }
    setQuestions(values);
  };

  const handleAddQuestion = () => {
    setQuestions([
      ...questions,
      { question: '', options: ['', '', '', ''], answer: 0, hasOptions: true }
    ]);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    
    try {
      // Validate questions
      const hasValidQuestions = questions.every(q => {
        if (!q.question.trim()) return false;
        if (q.hasOptions) {
          return q.options.every(opt => opt.trim()) && typeof q.answer === 'number';
        }
        return true;
      });

      if (!hasValidQuestions) {
        throw new Error('Please fill in all required fields');
      }

      const formattedData = {
        questionId,
        questions: questions.map(q => ({
          question: q.question,
          options: q.hasOptions ? q.options : [],
          answer: q.hasOptions ? q.answer : 0,
          hasOptions: q.hasOptions
        }))
      };
    
      const response = await axios.post(' https://quiz-server-ghre.onrender.com/api/questions', formattedData);
      
      setMessage({ 
        text: `Quiz saved successfully! (${response.data.mcqCount} MCQ, ${response.data.audioCount} Audio questions)`, 
        type: 'success' 
      });
      
      // Reset form
      setQuestions([{ question: '', options: ['', '', '', ''], answer: 0, hasOptions: true }]);
      setQuestionId('');
    } catch (error) {
      setMessage({ 
        text: error.response?.data?.error || error.message || 'Error submitting questions', 
        type: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="quiz-form-container">
      <h2 className="form-title">Create New Quiz</h2>
      
      {message.text && (
        <div className={`message ${message.type}`} role="alert">
          {message.type === 'error' && <AlertCircle className="message-icon" />}
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit} className="quiz-form">
        <div className="quiz-id-section">
          <label htmlFor="questionId">Quiz ID</label>
          <input
            type="text"
            id="questionId"
            value={questionId}
            onChange={(e) => setQuestionId(e.target.value)}
            className="quiz-id-input"
            required
            placeholder="Enter unique quiz identifier"
          />
        </div>

        {questions.map((question, index) => (
          <div key={index} className="question-block">
            <div className="question-header">
              <h3>Question {index + 1}</h3>
              <button
                type="button"
                className={`toggle-button ${question.hasOptions ? 'has-options' : 'no-options'}`}
                onClick={() => toggleOptions(index)}
              >
                {question.hasOptions ? (
                  <>
                    <ToggleRight size={20} />
                    <span>Multiple Choice</span>
                  </>
                ) : (
                  <>
                    <ToggleLeft size={20} />
                    <span>Audio Response</span>
                  </>
                )}
              </button>
            </div>

            <div className="question-input-group">
              <input
                type="text"
                name="question"
                value={question.question}
                onChange={(event) => handleQuestionChange(index, event)}
                placeholder="Enter your question"
                required
                className="question-input"
              />
            </div>

            {question.hasOptions && (
              <>
                <div className="options-grid">
                  {question.options.map((option, optIndex) => (
                    <div key={optIndex} className="option-group">
                      <label htmlFor={`question-${index}-option-${optIndex}`}>
                        Option {optIndex + 1}
                      </label>
                      <input
                        type="text"
                        id={`question-${index}-option-${optIndex}`}
                        name={`option-${optIndex}`}
                        value={option}
                        onChange={(event) => handleQuestionChange(index, event)}
                        required
                        className="option-input"
                        placeholder={`Enter option ${optIndex + 1}`}
                      />
                    </div>
                  ))}
                </div>

                <div className="answer-select-group">
                  <label htmlFor={`question-${index}-answer`}>Correct Answer</label>
                  <select
                    id={`question-${index}-answer`}
                    name="answer"
                    value={question.answer}
                    onChange={(event) => handleQuestionChange(index, event)}
                    required
                    className="answer-select"
                  >
                    {question.options.map((option, optIndex) => (
                      <option key={optIndex} value={optIndex}>
                        {option || `Option ${optIndex + 1}`}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}
          </div>
        ))}

        <div className="form-actions">
          <button 
            type="button" 
            className="add-button"
            onClick={handleAddQuestion}
          >
            <PlusCircle size={18} />
            Add Question
          </button>
          
          <button 
            type="submit" 
            className="submit-button"
            disabled={loading}
          >
            {loading ? (
              <span className="loading-spinner" />
            ) : (
              <>
                <Save size={18} />
                Save Quiz
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddQuestionsForm;
